/**
 *
 * Popover
 *
 */

import React from 'react';

// Monotonic counter giving each Popover instance a unique id to wire the
// trigger button to its disclosure panel via aria-controls (class component —
// no useId hook available here).
let popoverInstanceId = 0;

// Reset the UA button chrome so the trigger renders exactly like the plain
// element it wraps; the button exists purely to make the disclosure keyboard-
// operable (native Enter/Space) with an exposed expanded state.
const triggerResetStyle = {
  display: 'inline-block',
  background: 'none',
  border: 0,
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'inherit',
  textAlign: 'inherit',
  cursor: 'pointer',
};

class Popover extends React.PureComponent {
  state = { isOpen: false };

  contentId = `popover-content-${++popoverInstanceId}`;

  componentDidMount() {
    document.addEventListener('mousedown', this.onOutsideClick);

    // Enforce that every trigger has an accessible name: an icon-only trigger
    // (no rendered text) MUST be given a `triggerLabel`, otherwise screen
    // readers announce a nameless button. We can't apply a blanket fallback
    // aria-label (it would clobber the accessible name of text triggers like
    // "Filter"), so we surface the omission loudly in development instead.
    if (
      process.env.NODE_ENV !== 'production' &&
      !this.props.triggerLabel &&
      this.triggerRef &&
      !this.triggerRef.textContent.trim()
    ) {
      // eslint-disable-next-line no-console
      console.error(
        'Popover: icon-only trigger has no accessible name. Pass a `triggerLabel` prop so assistive technology can identify the control.'
      );
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onOutsideClick);
  }

  clickWasOutside = (target) => (
    target === this.refArea
      ? false
      : !!target
      ? this.clickWasOutside(target.parentNode)
      : true
  )

  onOutsideClick = (e) => {
    e.stopPropagation();

    if (this.state.isOpen && this.clickWasOutside(e.target)) {
      this.toggleIsOpen();
    }
  }

  toggleIsOpen = () => this.setState({ isOpen: !this.state.isOpen });

  render() {
    const { isOpen } = this.state;
    const {
      children,
      style,
      actions,
      triggerLabel,
      triggerClassName,
      // The kind of widget the disclosure opens. Defaults to "dialog" — the
      // popover reveals free-form panel content, NOT an ARIA menu widget (which
      // "true"/"menu" would falsely promise, making AT expect arrow-key menu
      // navigation that doesn't exist). Consumers that build a real listbox /
      // menu / grid / tree can override the token.
      triggerHasPopup = 'dialog',
    } = this.props;

    return (
      <div ref={(ref) => this.refArea = ref}>
        <button
          type="button"
          ref={(ref) => (this.triggerRef = ref)}
          className={triggerClassName}
          aria-haspopup={triggerHasPopup}
          aria-expanded={isOpen}
          aria-controls={this.contentId}
          aria-label={triggerLabel}
          onClick={this.toggleIsOpen}
          style={triggerResetStyle}>
          {children[0]}
        </button>
        <div id={this.contentId} style={{ zIndex: 1, position: 'relative', ...style }}>
          {isOpen ? (typeof children[1] === 'function' ? children[1](this.toggleIsOpen) : children[1]) : null}
          {actions ? actions(this.toggleIsOpen) : null}
        </div>
      </div>
    );
  }
}

export default Popover;
